import type { UseMutationResult } from "@tanstack/react-query";
import type { UseFormReturn } from "react-hook-form";

import { useEffect, useRef } from "react";

import { useIsRequestFormDirty } from "@/hooks/useIsRequestFormDirty";

type UseAutoSaveDraftProps<T extends Record<string, unknown>> = {
  draftId: number | undefined;
  formMethods: UseFormReturn<T>;
  isCreateDraftPending: boolean;
  isSubmitting: boolean;
  saveMutation: UseMutationResult<unknown, Error, T>;
};

export function useAutoSaveDraft<T extends Record<string, unknown>>({
  draftId,
  formMethods,
  isCreateDraftPending,
  isSubmitting,
  saveMutation,
}: UseAutoSaveDraftProps<T>) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedDataRef = useRef<string>("");
  const triggerSaveRef = useRef<(formData: T) => Promise<void>>();

  const setIsDirty = useIsRequestFormDirty((state) => state.setIsDirty);

  triggerSaveRef.current = async (formData: T) => {
    const currentDataString = JSON.stringify(formData);

    if (currentDataString === lastSavedDataRef.current) return;

    if (!draftId || isCreateDraftPending || isSubmitting) return;

    try {
      await saveMutation.mutateAsync(formData);
      lastSavedDataRef.current = currentDataString;
      setIsDirty(false);
    } catch (error) {
      console.error("Failed to autosave draft:", error);
    }
  };

  useEffect(() => {
    const subscription = formMethods.watch((data) => {
      const currentDataString = JSON.stringify(data);

      if (!lastSavedDataRef.current) {
        lastSavedDataRef.current = currentDataString;

        return;
      }

      if (currentDataString === lastSavedDataRef.current) return;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        void triggerSaveRef.current?.(data as T);
      }, 1500);
    });

    return () => {
      subscription.unsubscribe();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);

        const currentData = formMethods.getValues();
        const currentDataString = JSON.stringify(currentData);

        if (currentDataString !== lastSavedDataRef.current) {
          void triggerSaveRef.current?.(currentData as T);
        }
      }
    };
  }, []);
}
